/* eslint-disable react/destructuring-assignment */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { EditorState, convertToRaw, ContentState, Modifier } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { ActionCreators as UndoActionCreators } from 'redux-undo';
import htmlToDraft from 'html-to-draftjs';
import PropTypes from 'prop-types';
import { get as _get, isEmpty as _isEmpty } from 'lodash';
import eventService from '../../../../../../../../common/eventService';
import emitter from '../../../../../../../../common/eventService/emitter';
import ErrorBoundary from '../../../../../../../../componentLibrary/ErrorBoundary';
import CustomFonts from '../../../../../../../../componentLibrary/FontSelect/CustomFonts';
import ElementErrorSign from '../../../../../../../../componentLibrary/ElementErrorSign';
import Link from './Link';
import Headers from './Headers';
import * as draftJSOptions from './draftJSOptions';
import * as columnItemActions from '../../../../../../../../actions/columnItem';
import * as selectedElementsActions from '../../../../../../../../actions/selectedElements';
import './paragraphStyles.css';
import { elementContentType } from '../../../../../../../../types';
import {
    supportedElements,
    removeUnsupportedAttributes,
    removeUnsupportedElements,
} from './HelperMethods/PostPasteProcess';
import {
    ERR_PARAGRAPH_UNSUPPORTED_TAGS,
    ERR_PARAGRAPH_TOC_NO_HEADERS,
    ERR_PARAGRAPH_EMPTY,
    ERR_PARAGRAPH_PASTE_PARSE,
} from '../../../../../../../../constants/Errors';
import elementTypes from '../../../../../../../../constants/elementTypes';

const createEditorState = (textMarkupValue) => {
    if (textMarkupValue && textMarkupValue.length > 0) {
        // htmlToDraft recognizes <ins> tags as underlines so we need to replace it back from text-decoration underline to <ins>
        const newTextMarkupValue = textMarkupValue
            .replace(/<span style="text-decoration:underline;">/g, '<ins>')
            .replace(/<\/span><!-- end underline -->/g, '</ins>')
            .replace(/\n$/g, '');

        const contentBlock = htmlToDraft(newTextMarkupValue);
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        const newEditorState = EditorState.createWithContent(contentState);
        if (contentBlock) {
            return newEditorState;
        }
    }

    return EditorState.createEmpty();
};

class ParagraphElement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            showEditorModal: false,
            editorState: EditorState.createEmpty(),
            textMarkupValue: '',
            align: '',
            fontFamily: '',
            fontID: null,
            fontSize: '',
            lineHeight: '',
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: 0,
            paddingLeft: 0,
        };
    }

    componentDidMount() {
        document.execCommand('enableObjectResizing', false, false);
        const { elmIdx } = this.props;
        emitter.on(`GENERATE_TABLE_OF_CONTENTS-${elmIdx}`, this.onGenerateTableOfContents);
        emitter.on(`EDIT_SOURCE_CODE-${elmIdx}`, this.onEditSourceCode);
    }

    componentWillUnmount() {
        const { elmIdx } = this.props;
        emitter.off(`GENERATE_TABLE_OF_CONTENTS-${elmIdx}`, this.onGenerateTableOfContents);
        emitter.off(`EDIT_SOURCE_CODE-${elmIdx}`, this.onEditSourceCode);
    }

    onGenerateTableOfContents = (type) => this.generateTOC(type);

    onEditSourceCode = (html) => this.updateSourceCode(html);

    static getDerivedStateFromProps(nextProps, prevState) {
        const { content } = nextProps;
        const {
            textMarkupValue,
            align,
            fontFamily,
            fontID,
            fontSize,
            lineHeight,
            paddingTop,
            paddingRight,
            paddingBottom,
            paddingLeft,
        } = content;
        const newState = {
            ...prevState,
            textMarkupValue,
            align,
            fontFamily,
            fontID,
            fontSize,
            lineHeight,
            paddingTop,
            paddingRight,
            paddingBottom,
            paddingLeft,
        };
        if (textMarkupValue !== prevState.textMarkupValue) {
            const editorState = createEditorState(textMarkupValue);
            return { ...newState, editorState };
        }

        return { ...newState };
    }

    handlePastedText = (text, html, editorState, callback) => {
        if (html) {
            const parsedHTML = new DOMParser().parseFromString(html, 'text/html').body;
            const htmlCollection = parsedHTML.querySelectorAll('*');
            const htmlElements = [];
            htmlCollection.forEach((element) => {
                const tag = element.tagName.toLowerCase();
                if (!htmlElements.includes(tag)) {
                    htmlElements.push(tag);
                }
            });
            try {
                htmlElements.forEach((element) => {
                    if (supportedElements.includes(element)) {
                        removeUnsupportedAttributes(parsedHTML, element);
                    } else {
                        removeUnsupportedElements(parsedHTML, element);
                    }
                });

                const contentBlock = htmlToDraft(parsedHTML.outerHTML);
                const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                const newEditorState = EditorState.createWithContent(contentState);
                const newBlock = newEditorState.getCurrentContent().getBlockMap();
                const insertNewBlocksStateState = Modifier.replaceWithFragment(
                    editorState.getCurrentContent(),
                    editorState.getSelection(),
                    newBlock
                );

                const finalEditorState = EditorState.push(editorState, insertNewBlocksStateState, 'insert-fragment');
                this.cleanUpEditorStateAfterPaste(finalEditorState);
                callback(null, finalEditorState);
            } catch (err) {
                callback(err);
            }
        } else {
            callback(null, text);
        }
    };

    updateSourceCode = (html) => {
        const sourceCodeSupportedElements = [
            'a',
            'p',
            'span',
            'ol',
            'ul',
            'li',
            'h1',
            'h2',
            'h3',
            'strong',
            'em',
            'br',
            'sub',
            'sup',
            'div',
            'text',
            'body',
            'head',
            'html',
            'ins',
            'del',
        ];
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const htmlCollection = doc.querySelectorAll('*');
        const htmlElements = [];
        htmlCollection.forEach((element) => {
            const tag = element.tagName.toLowerCase();

            if (!htmlElements.includes(tag)) {
                htmlElements.push(tag);
            }
        });

        const unsupportedElementsExist = htmlElements.some((element) => !sourceCodeSupportedElements.includes(element));
        if (unsupportedElementsExist) {
            eventService.notify(ERR_PARAGRAPH_UNSUPPORTED_TAGS, true);
            return;
        }

        const editorState = createEditorState(html);
        this.setState({ editorState });

        // have to send editorState here since setState is async
        this.applyChanges(editorState);
    };

    generateTOC(data) {
        const { blocks, type } = data;
        let table = '';
        const canvasContainer = document.getElementById('canvasContainer');
        const headerList = canvasContainer.querySelectorAll('h1,h2,h3');
        if (headerList.length === 0) {
            eventService.notify(ERR_PARAGRAPH_TOC_NO_HEADERS, true);
            return;
        }
        blocks.forEach((block) => {
            const { columns } = block.content;
            columns.forEach((column) => {
                const { columnItems } = column;
                columnItems.forEach((item) => {
                    const elmType = item.type;
                    if (elmType === elementTypes.PARAGRAPH) {
                        const elmIndx = item.elmIdx;
                        const elmText = _get(item, 'content.textMarkupValue', '');
                        const parsedHTML = new DOMParser().parseFromString(elmText, 'text/html');
                        const headers = parsedHTML.body.querySelectorAll('h1, h2, h3');
                        headers.forEach((header) => {
                            const headerText = header.innerText.trim();
                            if (headerText.length > 0) {
                                if (type === 'list') {
                                    table += `<li><a href="#${elmIndx}">${headerText}</a></li><br>`;
                                } else {
                                    table += `<a href="#${elmIndx}">${headerText}</a> | `;
                                }
                            }
                        });
                    }
                });
            });
        });
        table = type === 'list' ? table.slice(0, -4) : table.slice(0, -2);
        table = `<p><strong>${table}</strong></p>`;
        this._addTableOfContent(table);
    }

    cleanUpEditorStateAfterPaste(editorState) {
        const { blockIndex, columnIdx, elmIdx, content, dispatch } = this.props;
        const newMarkupString = draftToHtml(convertToRaw(editorState.getCurrentContent()))
            .replace(new RegExp('<br>', 'g'), ' ')
            .replace(new RegExp('&nbsp;', 'g'), '');
        const newContent = { ...content, textMarkupValue: newMarkupString };
        dispatch(columnItemActions.updateColumnItemContent({ blockIndex, columnIdx, elmIdx, newContent }));
    }

    applyChanges(editorNewState) {
        const newState = editorNewState || this.state.editorState;
        const { blockIndex, columnIdx, elmIdx, content, dispatch } = this.props;
        const oldMarkup = content.textMarkupValue;
        const newMarkupString = draftToHtml(convertToRaw(newState.getCurrentContent()));
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(newMarkupString, 'text/html');
        if (htmlDoc.body.innerText.replace(/\s/g, '').length === 0) {
            eventService.notify(ERR_PARAGRAPH_EMPTY, true);
            this.setState({ editorState: createEditorState(oldMarkup) });
            return;
        }

        if (oldMarkup !== newMarkupString) {
            const newContent = { ...content, textMarkupValue: newMarkupString };
            dispatch(columnItemActions.updateColumnItemContent({ blockIndex, columnIdx, elmIdx, newContent }));
        }
    }

    _addTableOfContent(type) {
        const { textMarkupValue } = this.state;
        const newMarkup = `${textMarkupValue}\n${type}`;
        const editorState = createEditorState(newMarkup);
        this.setState({ editorState });
    }

    // Draft-js inserts a <br> on a carriage return (shift+enter). This overrides that behaviour to treat it like a regular return.
    // eslint-disable-next-line class-methods-use-this
    handleReturn() {
        return 'not-handled';
    }

    render() {
        const {
            elmIdx,
            elementIsSelected,
            multipleElementsSelected,
            leftPanelCollapsed,
            isCustomFontsLoaded,
            customFonts,
            dispatch,
            flags,
        } = this.props;
        const {
            hasError,
            editorState,
            align,
            fontFamily,
            fontID,
            fontSize,
            lineHeight,
            paddingTop,
            paddingRight,
            paddingBottom,
            paddingLeft,
        } = this.state;
        const paragraphElement = document.getElementsByClassName(`column-item-${elmIdx}`)[0];

        const styleMap = {
            BOLD: {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },
        };

        // eslint-disable-next-line no-nested-ternary
        const paragraphElementTopPosition = paragraphElement
            ? +paragraphElement.getBoundingClientRect().top > 0
                ? +paragraphElement.getBoundingClientRect().top
                : 90
            : 0;
        const { innerWidth } = window;

        return (
            <table style={{ width: '100%' }} cellSpacing="0" cellPadding="0">
                <tbody>
                    <tr>
                        {
                            /*
                             * We are short-circuiting here if custom fonts are not loaded and if the element does not have fontID in the blocks data.
                             * If these two conditions pass, we check among all the custom fonts if the fontFamily and fontID that are in this element
                             * can be found. If not, either the value from the custom font was changed in the database or the fontID is not in the
                             * custom fonts array, which means that the font was unlinked from the users account.
                             */
                            isCustomFontsLoaded &&
                                fontID &&
                                !customFonts.some(({ fontID: id, value }) => id === fontID && value === fontFamily) && (
                                    <ElementErrorSign
                                        dataFor="paragraph-custom-fonts-warning"
                                        tooltipMsg="Reselect your custom font in the text tool bar. If it isn’t on the list, contact your Account Manager to add it."
                                    />
                                )
                        }
                        <td
                            align={align}
                            verticalalign="middle"
                            style={{
                                fontSize: hasError ? 14 : 0,
                                paddingTop,
                                paddingRight,
                                paddingBottom,
                                paddingLeft,
                                wordBreak: 'break-word',
                            }}
                        >
                            <table
                                align={align}
                                border="0"
                                cellPadding="0"
                                cellSpacing="0"
                                role="presentation"
                                style={{ borderCollapse: 'separate', width: '100%', lineHeight: '100%' }}
                            >
                                <tbody>
                                    <tr>
                                        <td align="center" valign="middle" role="presentation">
                                            {hasError ? (
                                                <p style={{ color: 'red', fontWeight: 600 }}>
                                                    An error occurred in the paragraph element, undoing last action...
                                                </p>
                                            ) : (
                                                <ErrorBoundary
                                                    onError={() => {
                                                        dispatch(selectedElementsActions.removeAllSelectedElements());
                                                        this.setState({ hasError: true });
                                                        setTimeout(() => {
                                                            // TODO: redo more elegantly than dispatching same action twice
                                                            dispatch(UndoActionCreators.redo());
                                                            dispatch(UndoActionCreators.redo());
                                                            this.setState({ hasError: false });
                                                        }, 2500);
                                                    }}
                                                >
                                                    {flags.ed421BugfixDraftjsAutosave ? (
                                                        <Editor
                                                            spellCheck
                                                            defaultEditorState={editorState}
                                                            onEditorStateChange={(newState) =>
                                                                this.setState({ editorState: newState })
                                                            }
                                                            onBlur={() => this.applyChanges()}
                                                            onChange={() => this.applyChanges()}
                                                            customeStyleMap={styleMap}
                                                            toolbarHidden={
                                                                !elementIsSelected || multipleElementsSelected
                                                            }
                                                            handleReturn={this.handleReturn}
                                                            handlePastedText={(text, html) => {
                                                                this.handlePastedText(
                                                                    text,
                                                                    html,
                                                                    editorState,
                                                                    (err, finalEditorState) => {
                                                                        if (err) {
                                                                            // eslint-disable-next-line no-console
                                                                            console.error(err);
                                                                            eventService.notify(
                                                                                ERR_PARAGRAPH_PASTE_PARSE,
                                                                                true
                                                                            );
                                                                            return;
                                                                        }
                                                                        this.setState({
                                                                            editorState: finalEditorState,
                                                                        });
                                                                    }
                                                                );
                                                                return !!html;
                                                            }}
                                                            toolbar={draftJSOptions.toolbar(editorState, () =>
                                                                this.applyChanges()
                                                            )}
                                                            toolbarStyle={draftJSOptions.toolbarStyle(
                                                                leftPanelCollapsed,
                                                                innerWidth,
                                                                paragraphElementTopPosition
                                                            )}
                                                            editorStyle={{
                                                                color: 'initial',
                                                                margin: '0 auto',
                                                                marginTop: 0,
                                                                marginBottom: 0,
                                                                fontFamily,
                                                                fontSize,
                                                                lineHeight,
                                                                textAlign: align,
                                                                cursor: elementIsSelected ? 'text' : 'default',
                                                                overflow: 'visible',
                                                            }}
                                                            toolbarCustomButtons={[<Link />, <Headers />]}
                                                            handleDrop={() => 'handled'} // this is important, otherwise dropping other elements will trigger draftJS
                                                        />
                                                    ) : (
                                                        <Editor
                                                            spellCheck
                                                            editorState={editorState}
                                                            onEditorStateChange={(newState) =>
                                                                this.setState({ editorState: newState })
                                                            }
                                                            onBlur={() => this.applyChanges()}
                                                            customeStyleMap={styleMap}
                                                            toolbarHidden={
                                                                !elementIsSelected || multipleElementsSelected
                                                            }
                                                            handleReturn={this.handleReturn}
                                                            handlePastedText={(text, html) => {
                                                                this.handlePastedText(
                                                                    text,
                                                                    html,
                                                                    editorState,
                                                                    (err, finalEditorState) => {
                                                                        if (err) {
                                                                            // eslint-disable-next-line no-console
                                                                            console.error(err);
                                                                            eventService.notify(
                                                                                ERR_PARAGRAPH_PASTE_PARSE,
                                                                                true
                                                                            );
                                                                            return;
                                                                        }
                                                                        this.setState({
                                                                            editorState: finalEditorState,
                                                                        });
                                                                    }
                                                                );
                                                                return !!html;
                                                            }}
                                                            toolbar={draftJSOptions.toolbar(editorState, () =>
                                                                this.applyChanges()
                                                            )}
                                                            toolbarStyle={draftJSOptions.toolbarStyle(
                                                                leftPanelCollapsed,
                                                                innerWidth,
                                                                paragraphElementTopPosition
                                                            )}
                                                            editorStyle={{
                                                                color: 'initial',
                                                                margin: '0 auto',
                                                                marginTop: 0,
                                                                marginBottom: 0,
                                                                fontFamily,
                                                                fontSize,
                                                                lineHeight,
                                                                textAlign: align,
                                                                cursor: elementIsSelected ? 'text' : 'default',
                                                                overflow: 'visible',
                                                            }}
                                                            toolbarCustomButtons={[<Link />, <Headers />]}
                                                            handleDrop={() => 'handled'} // this is important, otherwise dropping other elements will trigger draftJS
                                                        />
                                                    )}
                                                </ErrorBoundary>
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    }
}

const mapStateToProps = (state) => {
    const hasCustomFontsEnabled = _get(state, 'session.user.features.EMAIL_DESIGNER_CUSTOM_FONTS', false);
    const isCustomFontsLoaded = _get(state, 'customFonts.isLoaded', false);
    const customFontsData = _get(state, 'customFonts.data', {});

    return {
        leftPanelCollapsed: _get(state, 'browser.leftPanelCollapsed', true),
        isCustomFontsLoaded,
        customFonts:
            hasCustomFontsEnabled && isCustomFontsLoaded && !_isEmpty(customFontsData)
                ? new CustomFonts(customFontsData)
                : [],
    };
};

ParagraphElement.propTypes = {
    blockIndex: PropTypes.string.isRequired,
    columnIdx: PropTypes.string.isRequired,
    content: elementContentType.isRequired,
    elementIsSelected: PropTypes.bool.isRequired,
    elmIdx: PropTypes.string.isRequired,
    leftPanelCollapsed: PropTypes.bool.isRequired,
    multipleElementsSelected: PropTypes.bool.isRequired,
    isCustomFontsLoaded: PropTypes.bool.isRequired,
    customFonts: PropTypes.array.isRequired,
    dispatch: PropTypes.func.isRequired,
    flags: PropTypes.oneOfType([PropTypes.objectOf(PropTypes.bool), PropTypes.object]),
};

ParagraphElement.defaultProps = {
    flags: {},
};

export default withLDConsumer()(connect(mapStateToProps)(ParagraphElement));
